import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { portfolioHref } from "../lib/routes";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh, user } = useAuth();
  const { t } = useLang();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 7) {
        setState("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("success");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        // fall through and retry
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
            {t("checking_payment")}
          </div>
          <div className="inline-flex h-12 w-12 items-center justify-center border border-sienna-400 text-sienna-400 animate-pulse font-mono">●</div>
        </>
      )}
      {state === "success" && (
        <div data-testid="payment-success-state">
          <CheckCircle2 size={52} className="mx-auto text-sienna-400" />
          <h1 className="mt-8 font-serif text-5xl text-zinc-50 font-light tracking-tight">
            {t("payment_success_title")}
          </h1>
          <p className="mt-5 text-zinc-300 font-light">{t("payment_success_body")}</p>
          <Link
            to={user && user.id ? portfolioHref(user) : "/"}
            className="mt-10 inline-block bg-sienna text-white px-8 py-3 text-sm font-light hover:bg-sienna-400 transition-colors"
            data-testid="go-to-portfolio-link"
          >
            {t("go_to_portfolio")}
          </Link>
        </div>
      )}
      {(state === "failed" || state === "timeout") && (
        <div data-testid="payment-failed-state">
          <XCircle size={52} className="mx-auto text-zinc-400" />
          <h1 className="mt-8 font-serif text-4xl text-zinc-50 font-light tracking-tight">
            {t("payment_failed")}
          </h1>
          <Link
            to="/upgrade"
            className="mt-10 inline-block border border-zinc-800 text-zinc-100 px-8 py-3 text-sm font-light hover:bg-zinc-900"
          >
            {t("upgrade")}
          </Link>
        </div>
      )}
    </div>
  );
}
